import { MONTH_NAMES } from './schedule.constants';

/** Parse 'HH:MM' into fractional hours (e.g. '10:30' → 10.5). */
export function parseHour(t: string | undefined): number {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h + (m ?? 0) / 60;
}

/**
 * Parse a closing time for grids. `00:00` after a real open means midnight (24:00).
 */
export function parseCloseHour(close: string | undefined, open?: string): number {
  if (!close) {
    return 22;
  }
  if (close === '24:00') {
    return 24;
  }
  if (close === '00:00' && open && open !== '00:00') {
    return 24;
  }
  return parseHour(close);
}

/** Monday = 0 … Sunday = 6. */
export function dayOfWeekMon0(date: Date): number {
  return (date.getDay() + 6) % 7;
}

/** Local calendar date as 'YYYY-MM-DD'. */
export function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Format a 30-min slot start time. */
export function slotTime(hour: number, half: 0 | 1): string {
  return `${String(hour).padStart(2, '0')}:${half === 0 ? '00' : '30'}`;
}

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday of the week containing `d`. */
export function mondayOf(d: Date): Date {
  const base = startOfDay(d);
  base.setDate(base.getDate() - dayOfWeekMon0(base));
  return base;
}

/** Seven dates starting at `weekStart` (Monday). */
export function weekDatesFromMonday(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}

/** Coerce Firestore / string date fields to 'YYYY-MM-DD'. */
export function normalizeDateValue(value: unknown): string {
  if (typeof value === 'string') return value.slice(0, 10);
  if (value && typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return toDateStr((value as { toDate: () => Date }).toDate());
  }
  if (value instanceof Date) return toDateStr(value);
  return '';
}

export function normalizeStatus(value: unknown): string {
  return typeof value === 'string' ? value.trim().toUpperCase() : '';
}

export function isToday(date: Date): boolean {
  const t = new Date();
  return date.getFullYear() === t.getFullYear()
      && date.getMonth()    === t.getMonth()
      && date.getDate()     === t.getDate();
}

export function formatWeekRange(weekDates: Date[]): string {
  const fmt = (d: Date) => `${d.getDate()} ${MONTH_NAMES[d.getMonth()].slice(0, 3)}`;
  return `${fmt(weekDates[0])} – ${fmt(weekDates[6])} ${weekDates[6].getFullYear()}`;
}
